import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Query } from '../../design-system/types';
import { useGetDossierOverviews } from '../dossier-overview/hooks';
import { NormalizedAgents } from '../agent/types';
import { NormalizedDossier, DossierId } from '../dossier/types';
import { useAuth } from '../../shared/providers/AuthProvider';
import { useGetAgents } from '../agent/hooks';

import { NormalizedPreTripReview, AlertPreTripReviewsInformation, PreTripReview } from './types';
import { fetchPreTripReviews } from './api';

export const getPreTripReviewsQueryKey = () => ['preTripReview'];

const useGetPreTripReviews = () => {
    return useQuery({
        queryKey: getPreTripReviewsQueryKey(),
        queryFn: () => fetchPreTripReviews(),
    });
};

const getUnsatisfiedTravelersPreTripReviews = (params: {
    preTripReviews: NormalizedPreTripReview;
    dossiers: NormalizedDossier;
    agents: NormalizedAgents;
    query: Query;
    userId: number | string | null;
}): AlertPreTripReviewsInformation[] => {
    const { preTripReviews, dossiers, agents, query, userId } = params;
    return Object.entries(preTripReviews)
        .filter(([dossierId, preTripReview]: [DossierId, PreTripReview]) => {
            if (
                Object.keys(dossiers).length === 0 ||
                !dossiers[dossierId] ||
                !dossiers[dossierId].agentId
            ) {
                return false;
            }

            const { agentId } = dossiers[dossierId];

            const filteredByConnectedAgent =
                !preTripReview.isTravelerSatisfied && userId?.toString() === agentId;

            if (!query.agentId) {
                return filteredByConnectedAgent;
            }

            const filteredByAgent = query.agentId !== 'all' && query.agentId?.includes(agentId);
            const filteredByAllAgents = query.agentId === 'all';

            return !preTripReview.isTravelerSatisfied && (filteredByAgent || filteredByAllAgents);
        })
        .map(([dossierId, preTripReview]: [DossierId, PreTripReview]) => {
            const { project, agentId } = dossiers[dossierId];

            return {
                dossierId: dossierId,
                createdAt: preTripReview.createdAt,
                requesterName: project.requesterName,
                agentPhotoUrl: agents[agentId].photoUrl,
                agentName: agents[agentId].name,
            };
        })
        .sort((alertPreTripReviewInformationA, alertPreTripReviewInformationB) => {
            if (
                alertPreTripReviewInformationA.createdAt &&
                alertPreTripReviewInformationB.createdAt
            ) {
                return (
                    alertPreTripReviewInformationB.createdAt.getTime() -
                    alertPreTripReviewInformationA.createdAt.getTime()
                );
            }
            return 0;
        });
};

export const useGetAlertPreTripReviewsInformation = (query: Query) => {
    const { data: dossiers } = useGetDossierOverviews();
    const { data: agents } = useGetAgents();
    const { data: preTripReviews } = useGetPreTripReviews();

    const { userId } = useAuth();

    return useMemo(() => {
        if (!preTripReviews || !dossiers || !agents) return [];

        return getUnsatisfiedTravelersPreTripReviews({
            preTripReviews,
            dossiers,
            agents,
            query,
            userId,
        });
    }, [preTripReviews, dossiers, agents, query, userId]);
};
